import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Text, TextInput } from "@mantine/core"
import { IconSearch } from "@tabler/icons-react"
import { useCallback, useId, useMemo, useState } from "react"
import { SETTINGS_GROUP_META } from "../groups"
import type { ISettingsIndexEntry } from "../settings-index"
import type { IProps } from "./interfaces"
import classes from "./styles.module.css"

const MAX_RESULTS = 8

/**
 * Settings search — a static, typed index (no DOM scraping). Matches the query
 * against each card's translated title + description plus locale-agnostic keyword
 * aids (AND across query tokens). Arrow keys move the active result, Enter jumps,
 * Escape clears. Purely presentational: the parent owns the jump (group switch +
 * scroll + highlight).
 */
export const SettingsSearch = ({ entries, onJump }: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const [query, setQuery] = useState<string>("")
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const [searchUnlocked, setSearchUnlocked] = useState<boolean>(false)
  const listId = useId()

  const groupLabel = useMemo<Record<string, string>>(() => {
    const out: Record<string, string> = {}
    for (const g of SETTINGS_GROUP_META) out[g.value] = i18n._(g.label)
    return out
  }, [i18n])

  // Precomputed lowercase haystack per entry: title + description + keywords.
  const haystacks = useMemo<Map<ISettingsIndexEntry, string>>(() => {
    const map = new Map<ISettingsIndexEntry, string>()
    for (const entry of entries) {
      const parts = [i18n._(entry.title), i18n._(entry.description), ...entry.keywords]
      map.set(entry, parts.join(" ").toLowerCase())
    }
    return map
  }, [entries, i18n])

  const results = useMemo<ISettingsIndexEntry[]>(() => {
    const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
    if (tokens.length === 0) return []
    const matches = entries.filter((entry) => {
      const hay = haystacks.get(entry) ?? ""
      return tokens.every((t) => hay.includes(t))
    })
    return matches.slice(0, MAX_RESULTS)
  }, [query, entries, haystacks])

  const open = query.trim().length > 0

  const jump = useCallback(
    (entry: ISettingsIndexEntry): void => {
      onJump(entry.group, entry.anchor)
      setQuery("")
      setActiveIndex(0)
    },
    [onJump],
  )

  const onQueryChange = useCallback((value: string): void => {
    setQuery(value)
    setActiveIndex(0)
  }, [])

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === "Escape") {
        setQuery("")
        setActiveIndex(0)
        return
      }
      if (results.length === 0) return
      if (e.key === "ArrowDown") {
        e.preventDefault()
        setActiveIndex((i) => Math.min(i + 1, results.length - 1))
      } else if (e.key === "ArrowUp") {
        e.preventDefault()
        setActiveIndex((i) => Math.max(i - 1, 0))
      } else if (e.key === "Enter") {
        const entry = results[activeIndex]
        if (entry) {
          e.preventDefault()
          jump(entry)
        }
      }
    },
    [results, activeIndex, jump],
  )

  return (
    <div className={classes.root}>
      <TextInput
        value={query}
        onChange={(e) => onQueryChange(e.currentTarget.value)}
        onKeyDown={onKeyDown}
        leftSection={<IconSearch size={16} />}
        placeholder={i18n._(msg`Search settings…`)}
        aria-label={i18n._(msg`Search settings`)}
        autoComplete="new-password"
        name="lex-settings-jump"
        type="text"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        readOnly={!searchUnlocked}
        onFocus={() => setSearchUnlocked(true)}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        size="sm"
      />

      {open && (
        <div className={classes.results} id={listId} role="listbox">
          {results.length === 0 ? (
            <div className={classes.empty}>
              <Text size="sm" c="dimmed">
                {i18n._(msg`No matching settings`)}
              </Text>
            </div>
          ) : (
            results.map((entry, index) => (
              <button
                type="button"
                key={entry.anchor}
                role="option"
                aria-selected={index === activeIndex}
                className={classes.result}
                data-active={index === activeIndex || undefined}
                // Keep the active row in sync with pointer hover so Enter and click agree.
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => jump(entry)}
              >
                <Text size="sm" fw={600} className={classes.resultTitle}>
                  {i18n._(entry.title)}
                </Text>
                <Text size="xs" c="dimmed">
                  {groupLabel[entry.group]}
                </Text>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
